// THE GOLDEN THREAD (Track Golden Thread.html, turns 1–3, 04/09/2026).
//
// One continuous history of a relationship, oldest first, ending at today. Every
// record is a node on the line and every gap is a visible break in it, with the
// chase sitting in the break.
//
// It replaces a panel that listed linked records in four flat groups — a
// directory, which is a thing you consult rather than a thing that tells you
// something. The line is the argument: where the record stops, the line breaks.
//
// FOUR RECORD TYPES, AND ONLY FOUR (turn 2). Enquiries and fees, instructions
// and site decisions, invoices and payments, meeting notes and calls. Drawings
// and emails are deliberately NOT on it — they are filing, and a thread that
// carries everything says nothing about whether the story holds together.
//
// THREE RULES THIS FILE EXISTS TO HOLD, because each is easy to get subtly and
// expensively wrong:
//
//   1. A YOUNG THREAD IS NOT A BROKEN ONE. Three records on a job opened
//      yesterday is right, and "0 of 17" would be a lie. The score counts what
//      the thread HAS against what it has reason to expect, never against an
//      imagined complete history.
//   2. HIDDEN MUST NOT LOOK BROKEN. A record withheld at your access level
//      keeps the solid line and a grey node, and counts as PRESENT. Somebody
//      else can see it; the relationship is intact and the thread must say so.
//   3. A BREAK NAMES ITS OWNER. "The 800mm wall was never turned into an
//      instruction" — with who took the call and how long it has sat. A gap
//      nobody owns is a complaint.

export const KINDS = ['enquiry', 'instruction', 'invoice', 'note'];
/** The legend's four pairs. A node says the SPECIFIC thing it is (a call, a
 *  payment, a site decision); the legend says the four types they fall into. */
export const KIND_LABEL = {
  enquiry: 'ENQUIRY & FEE',
  instruction: 'INSTRUCTION & SITE DECISION',
  invoice: 'INVOICE & PAYMENT',
  note: 'MEETING NOTE & CALL',
};
/** The legend's four dots, in the canvas's order. */
export const KIND_TONE = {
  enquiry: 'var(--anchor)',
  instruction: 'var(--brand-blue)',
  invoice: 'var(--anchor)',
  note: 'var(--ink-2)',
};

/** Nothing added for this long and the thread says so rather than going quiet. */
export const QUIET_DAYS = 14;

/** What a withheld record says about itself: that it is there, and its type. */
const WITHHELD_EYEBROW = {
  enquiry: 'Fee proposal', instruction: 'Instruction', invoice: 'Invoice', note: 'Note',
};
const WITHHELD_TITLE = {
  enquiry: 'A fee record sits here', instruction: 'An instruction sits here',
  invoice: 'An invoice record sits here', note: 'A note sits here',
};

const asTime = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v === 'object' && v.seconds) return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
};
const daysBetween = (a, b) => Math.floor((b - a) / 86400000);

/**
 * One node. `withheld` means the record exists and this person may not read it:
 * it keeps its place, its line and its count, and says only that it is there.
 */
function node(kind, o) {
  return {
    kind,
    // What this record IS, in the practice's words — the canvas node eyebrows
    // read "Site decision", "Call", "Payment", never the type they belong to.
    eyebrow: o.withheld ? WITHHELD_EYEBROW[kind] : (o.eyebrow || KIND_LABEL[kind]),
    id: o.id,
    at: o.at,
    title: o.withheld ? WITHHELD_TITLE[kind] : o.title,
    meta: o.withheld ? o.withheldMeta || 'On the thread and accounted for.' : o.meta,
    chips: o.withheld ? [] : (o.chips || []).filter(Boolean),
    withheld: !!o.withheld,
    ref: o.ref || null,
    // Who put it there, carried through so a break downstream can name them.
    byName: o.withheld ? null : (o.byName || null),
    byId: o.withheld ? null : (o.byId || null),
    record: o.withheld ? null : (o.record || null),
  };
}

/**
 * Build the thread from the records that belong to it.
 *
 * @param src.enquiries    the client's enquiries (each with number, receivedAt, quotedFee)
 * @param src.instructions this project's instructions
 * @param src.invoices     invoices raised against it
 * @param src.notes        meeting notes and calls
 * @param src.canSee       (record) => boolean — access, defaulting to everything
 */
export function buildThread(src = {}) {
  const { enquiries = [], instructions = [], invoices = [], notes = [], canSee = () => true } = src;
  const out = [];

  for (const e of enquiries) {
    const at = asTime(e.receivedAt || e.createdAt);
    if (!at) continue;
    out.push(node('enquiry', {
      id: `enq:${e.id || e.number}`, at, withheld: !canSee(e), ref: e.number || null,
      byName: e.byName || e.loggedBy || null, byId: e.byId || null, record: e,
      eyebrow: 'Enquiry',
      title: e.title || e.subject || `Enquiry ${e.number || ''}`.trim(),
      meta: [e.clientName, e.status].filter(Boolean).join(' · '),
      chips: [e.quotedFee ? `£${Number(e.quotedFee).toLocaleString('en-GB')}` : null],
    }));
  }
  for (const i of instructions) {
    const at = asTime(i.instructedAt || i.createdAt || i.at);
    if (!at) continue;
    out.push(node('instruction', {
      id: `instr:${i.id || i.seq}`, at, withheld: !canSee(i), ref: i.seq != null ? `#${i.seq}` : null,
      byName: i.byName || i.by || i.raisedBy || null, byId: i.byId || null, record: i,
      eyebrow: /\b(agreed|decided|on site|revis)/i.test(i.description || '') ? 'Site decision' : 'Instruction',
      title: i.description || i.title || 'Instruction',
      meta: [i.status, i.by || i.raisedBy].filter(Boolean).join(' · '),
      chips: [i.seq != null ? `#${i.seq}` : null, i.fee ? `£${Number(i.fee).toLocaleString('en-GB')}` : null],
    }));
  }
  for (const v of invoices) {
    const at = asTime(v.invoiceDate || v.createdAt);
    if (!at) continue;
    const paid = String(v.paymentStatus || '').toLowerCase() === 'paid';
    out.push(node('invoice', {
      id: `inv:${v.id || v.number}`, at, withheld: !canSee(v), ref: v.number || null,
      byName: v.byName || v.raisedBy || null, byId: v.byId || null, record: v,
      eyebrow: paid ? 'Payment' : 'Invoice',
      title: `Invoice ${v.number || ''}`.trim(),
      meta: v.paidDate ? `Paid ${new Date(asTime(v.paidDate)).toLocaleDateString('en-GB')}`
        : (v.paymentStatus || 'Raised'),
      chips: [v.amount ? `£${Number(v.amount).toLocaleString('en-GB')}` : null],
    }));
  }
  for (const n of notes) {
    const at = asTime(n.at || n.createdAt);
    if (!at) continue;
    out.push(node('note', {
      id: `note:${n.id}`, at, withheld: !canSee(n),
      byName: n.byName || n.by || null, byId: n.byId || null, record: n,
      eyebrow: /site visit/i.test(n.kind || '') ? 'Site visit'
        : /call/i.test(n.kind || '') ? 'Call' : /meeting/i.test(n.kind || '') ? 'Meeting' : 'Note',
      title: n.title || n.text || 'Note',
      meta: n.byName || n.by || '',
      chips: [],
    }));
  }
  // Oldest first, ending at today — the line reads as a history, not a feed.
  return out.sort((a, b) => a.at - b.at);
}

/**
 * What the thread has reason to expect next, given what is on it.
 *
 * Deliberately NOT a fixed checklist of every record a job could ever have:
 * a young thread would score terribly against one, and the whole point is that
 * a job opened yesterday with two records is healthy. Each expectation is only
 * raised once the thing before it exists.
 */
export function expectations(nodes = [], { hasFee = false, fee = null } = {}) {
  const has = (k) => nodes.some((n) => n.kind === k);
  const out = [];
  if (!has('enquiry')) out.push({ key: 'enquiry', label: 'The enquiry this came from', hint: 'the front door' });
  else if (!hasFee) out.push({ key: 'fee', label: 'The fee, agreed and recorded', hint: 'before any work is billed' });
  if (!has('instruction')) out.push({ key: 'instruction', label: 'The first instruction or site decision', hint: 'usually within 2 weeks' });
  if (!has('note')) out.push({ key: 'note', label: 'A design team meeting or a call worth noting', hint: 'usually within a month' });
  if (!has('invoice')) {
    out.push({
      key: 'invoice',
      label: fee ? `The first invoice against the ${fee}` : 'The first invoice against the fee',
      hint: 'at stage 1',
    });
  }
  return out;
}

/**
 * The breaks: where the record stops. Each one names what is missing, who it
 * belongs to and how long it has sat — a gap nobody owns is a complaint, not a
 * chase.
 */
export function breaks(nodes = [], { now = Date.now(), hasFee = false, meId = null, meName = null } = {}) {
  const out = [];
  // A break is TRIGGERED by a record you can see — you cannot chase what you
  // cannot read, and a break with no name on it is a complaint. But a break is
  // SATISFIED by a record that exists, withheld or not: an invoice you are not
  // allowed to see is still an invoice, and telling you the work was never
  // billed because of your own access level is the exact lie rule 3e forbids.
  const visible = (k) => nodes.filter((n) => n.kind === k && !n.withheld);
  const exists = (k) => nodes.filter((n) => n.kind === k);

  // A decision taken on site that never became an instruction. This is the one
  // the whole feature is named for: the contractor builds from instructions,
  // and a decision that only exists as a note tells nobody anything.
  for (const n of visible('note')) {
    const laterInstruction = nodes.some((i) => i.kind === 'instruction' && i.at >= n.at);
    if (!laterInstruction && /\b(agreed|decided|instruct|confirm|change|revis)/i.test(n.title || '')) {
      out.push({
        key: `note-no-instruction:${n.id}`, after: n.at, days: daysBetween(n.at, now),
        title: 'A decision was never turned into an instruction',
        why: `${n.title} — the decision only exists as a note, and nothing on the project record tells the contractor to build it that way.`,
        owner: n.meta || null, ownerName: n.byName || null, ownerId: n.byId || null, raise: 'Raise the instruction',
      });
    }
  }
  // Work instructed and never billed.
  const lastInstr = visible('instruction').slice(-1)[0];
  if (lastInstr && !exists('invoice').some((v) => v.at >= lastInstr.at)) {
    const days = daysBetween(lastInstr.at, now);
    if (days >= QUIET_DAYS) {
      out.push({
        key: `instruction-no-invoice:${lastInstr.id}`, after: lastInstr.at, days,
        title: 'Instructed work has not been invoiced',
        why: `${lastInstr.title} was instructed ${days} days ago and nothing has been raised against it.`,
        owner: lastInstr.meta || null, ownerName: lastInstr.byName || null, ownerId: lastInstr.byId || null, raise: 'Raise the invoice',
      });
    }
  }
  // A fee that was never written down.
  if (nodes.some((n) => n.kind === 'enquiry') && !hasFee) {
    out.push({
      key: 'no-fee', after: nodes[0].at, days: daysBetween(nodes[0].at, now),
      title: 'No fee is recorded against this work',
      why: 'The enquiry is on the thread but nothing says what was agreed, so nothing can be checked against it later.',
      owner: null, raise: 'Record the fee',
    });
  }
  // Whose it is. A break with a name on it is a chase; one without is a
  // complaint about the office in general, which nobody ever acts on.
  for (const b of out) {
    const who = b.ownerName || (b.owner ? String(b.owner).split(' · ')[0] : '');
    b.ownerName = who || null;
    b.ownerInitials = initials(who);
    b.mine = !!(who && ((meName && who.toLowerCase() === String(meName).toLowerCase())
      || (meId && b.ownerId === meId)));
    b.ownerLine = b.mine ? 'Yours to fix' : (who ? `${who} to fix` : 'Nobody owns this yet');
  }
  return out.sort((a, b) => b.days - a.days);
}

/** Two letters, or one, or none — never a placeholder that reads as a person. */
export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * The score. `done of total`, where total is what the thread HAS plus what it
 * is still waiting for — never an imagined complete history.
 *
 * WITHHELD RECORDS COUNT AS PRESENT. They are on the thread; somebody else can
 * read them; the relationship is intact. Counting them as missing would make a
 * healthy thread look broken to whoever has the least access, which is exactly
 * backwards.
 */
export function score(nodes = [], expected = []) {
  const done = nodes.length;
  return { done, total: done + expected.length, expected: expected.length };
}

/** Nothing added for a fortnight — said plainly rather than left to be noticed. */
export function quietFor(nodes = [], now = Date.now()) {
  if (!nodes.length) return null;
  const last = nodes[nodes.length - 1].at;
  const days = daysBetween(last, now);
  return days >= QUIET_DAYS ? days : null;
}

/**
 * The summary card's own words (2a, 3c, 3d, 3e). Derived from the same numbers
 * the bar and the list are drawn from, so the card cannot say "complete" over a
 * list with a break in it — the prototype did exactly that.
 */
export function summaryOf({ score: s, breaks: gaps = [], withheld = 0, quiet = null }) {
  const young = !gaps.length && s.expected > 0;
  const totalLabel = young ? `of ${s.total - s.expected} so far` : `of ${s.total}`;
  let gapNote; let tone; let raiseLabel = null;
  if (gaps.length) {
    const mine = gaps.filter((b) => b.mine).length;
    gapNote = mine === gaps.length && mine === 1 ? '1 break you can fix'
      : `${gaps.length} break${gaps.length === 1 ? '' : 's'}${mine ? `, ${mine} you can fix` : ''}`;
    tone = 'var(--brand-orange)';
    raiseLabel = `Raise ${gaps.length} task${gaps.length === 1 ? '' : 's'}`;
  } else if (s.expected) {
    gapNote = 'Complete for its age';
    tone = 'var(--anchor)';
  } else {
    gapNote = 'Thread complete';
    tone = 'var(--anchor)';
  }
  const sub = quiet ? `Nothing added for ${quiet} days.`
    : withheld ? `${withheld} record${withheld === 1 ? ' is' : 's are'} withheld at your access level.`
    : gaps.length ? null : 'nothing to chase';
  return { doneLabel: String(s.done), totalLabel, gapNote, tone, raiseLabel, sub };
}

/** Everything a panel needs, derived once so the header, the bar and the list
 *  cannot disagree with each other. */
export function threadFor(src = {}, { now = Date.now(), hasFee = false, fee = null, meId = null, meName = null } = {}) {
  const nodes = buildThread(src);
  const expected = expectations(nodes, { hasFee, fee });
  const gaps = breaks(nodes, { now, hasFee, meId, meName });
  const counts = KINDS.reduce((o, k) => { o[k] = nodes.filter((n) => n.kind === k).length; return o; }, {});
  const t = {
    nodes, expected, breaks: gaps, counts,
    score: score(nodes, expected),
    quiet: quietFor(nodes, now),
    healthy: gaps.length === 0,
    withheld: nodes.filter((n) => n.withheld).length,
  };
  t.summary = summaryOf(t);
  return t;
}
